import { FG_ACCENT_COLOR, FG_TEXT_COLOR } from '../../styles/generated/types';

// Source-of-truth for the `fg-color-*` foreground color utilities — the property-named
// successor to `text-color-*` (see RFC: RCIcon → utility component). Icons and text share
// this one family (both set `color`; icons via `fill: currentColor`). Each value maps
// losslessly to `.fg-color-{value}` → `var(--rc-color-{value})`.
//
// Split by contrast tier so consumers can gate what they expose:
//   - text-*   (4.5:1) body-text-safe — the full RCText-pickable set
//   - accent-* (3:1)   graphical / large-text only — icons + large text add these
//
// The two fixed tiers come from colors.utilities.json, which emits both the classes and
// these unions — one group per tier, so the tier distinction survives into the types.
// Re-exported under their plural names because that is what consumers import.

// Contrast tier: normal body text (WCAG 4.5:1).
export const FG_TEXT_COLORS = FG_TEXT_COLOR;

// Contrast tier: graphical objects / large text (WCAG 3:1). Valid for icons and large
// text, not body text — so RCText excludes these while RCIcon includes them.
export const FG_ACCENT_COLORS = FG_ACCENT_COLOR;

// Surface-tracking values: inside a themed ancestor they take the surface's family color
// (text-themed 4.5:1, accent-themed 3:1); elsewhere they inherit. Var-backed (they read
// the themed-surface `--_theme-*` vars) rather than a fixed `--rc-color-*` token, so they
// live apart from the two fixed tiers. `text-themed` belongs to the text tier, and
// `accent-themed` to the accent tier, for prop-union gating.
export const FG_THEMED_COLORS = ['text-themed', 'accent-themed'] as const;

export const FG_COLORS = [...FG_TEXT_COLORS, ...FG_ACCENT_COLORS, ...FG_THEMED_COLORS] as const;

export type FgTextColor = typeof FG_TEXT_COLORS[number];
export type FgAccentColor = typeof FG_ACCENT_COLORS[number];
export type FgThemedColor = typeof FG_THEMED_COLORS[number];
export type FgColor = typeof FG_COLORS[number];

// A foreground-color value → its utility class at one breakpoint (`prefix` is `''` for
// the base and `tablet-` / `mobile-` otherwise).
export function fgColorClass(color: FgColor, prefix = ''): string {
  return `${prefix}fg-color-${color}`;
}
